import styles from 'css/src/components/common/comments/VideoComment.module.scss';

import React, { useCallback, useEffect, useState } from 'react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { mergeRegister } from '@lexical/utils';
import type { SerializedEditorState, SerializedLexicalNode } from 'lexical';
import {
    $getRoot,
    $nodesOfType,
    CLEAR_EDITOR_COMMAND,
    COMMAND_PRIORITY_LOW,
    KEY_ENTER_COMMAND,
} from 'lexical';

import { MentionNode } from './MentionNode';

import { BUTTON_SIZES, PrimaryButton } from 'js/src/components/common/ui/Button';
import { addVideoComment, clearCommentDraft } from 'js/src/data/comments';
import { useAppDispatch } from 'js/src/libs/RootState';

export type onBeforeCommentingT = (
    callback: (obj: { external_user_name: string; external_user_email: string }) => void,
) => void;

type Props = {
    showing: boolean;
    videoId: number;
    videoSlug: string;
    slideId: string;
    videoOwner: number;
    onBeforeCommenting?: onBeforeCommentingT;
};

const isCommentValid = (textContent: string) => {
    return textContent.trim() !== '';
};

const CommentActionsPlugin: React.FC<Props> = React.memo(function CommentActionsPlugin({
    showing,
    videoId,
    videoSlug,
    slideId,
    videoOwner,
    onBeforeCommenting,
}) {
    const [editor] = useLexicalComposerContext();
    const dispatch = useAppDispatch();
    const [canPublish, setCanPublish] = useState(false);

    const handlePublishComment = useCallback(() => {
        const editorState = editor.getEditorState();
        const notifyUsers: Record<string, number | null> = {};
        editorState.read(() => {
            $nodesOfType(MentionNode).forEach((mentionNode: MentionNode) => {
                if (mentionNode.person.id === videoOwner) {
                    // the video owner will receive a different notification,
                    // something like "there was a new comment on your video",
                    // so we shouldn't also send them the "someone mentioned you in a comment"
                    // notification.
                    return;
                }
                notifyUsers[mentionNode.person.email] = mentionNode.person.id;
            });
        });
        const raw = editorState.toJSON();

        const dispatchAddVideoComment = ({
            videoId,
            videoSlug,
            slideId,
            message: raw,
            notifyUsers,
        }: {
            videoId: number;
            videoSlug: string;
            slideId: string;
            message: SerializedEditorState<SerializedLexicalNode>;
            notifyUsers: Record<string, number | null>;
        }) => {
            return ({ external_user_name = '', external_user_email = '' } = {}) => {
                dispatch(
                    addVideoComment({
                        video_id: videoId,
                        video_slug: videoSlug,
                        slide_id: slideId,
                        message: raw,
                        notifyUsers,
                        external_user_name,
                        external_user_email,
                    }),
                );

                dispatch(clearCommentDraft({ slide_id: slideId }));
                editor.dispatchCommand(CLEAR_EDITOR_COMMAND, undefined);
            };
        };

        if (onBeforeCommenting && typeof onBeforeCommenting === 'function') {
            onBeforeCommenting(
                dispatchAddVideoComment({
                    videoId,
                    videoSlug,
                    slideId,
                    message: raw,
                    notifyUsers,
                }),
            );
        } else {
            dispatchAddVideoComment({
                videoId,
                videoSlug,
                slideId,
                message: raw,
                notifyUsers,
            })();
            // extra `()` because on share-page, we return a callback to "suspend" the comment posting
            // in order to collect userName/email from non-Lumen5 users, so we're just running the callback
            // here since in the creator there are no anonymous users
        }
    }, [dispatch, editor, onBeforeCommenting, slideId, videoId, videoSlug, videoOwner]);

    useEffect(() => {
        // Seed from current editor content so a restored draft enables the button without retyping.
        // CommentDraftPlugin's mount effect runs before this one, so the draft is already loaded.
        const currentText = editor.getEditorState().read(() => $getRoot().getTextContent());
        setCanPublish(isCommentValid(currentText));

        return mergeRegister(
            editor.registerTextContentListener((textContent) => {
                setCanPublish(isCommentValid(textContent));
            }),
            editor.registerCommand<KeyboardEvent>(
                KEY_ENTER_COMMAND,
                (e: KeyboardEvent) => {
                    /*
                    BEGIN HACK!

                    Here, we want to submit the comment if the user presses the "enter"
                    key. There's just one gotcha: if the user is currently at-mentioning
                    someone (the menu of options of people to mention is open), then
                    we actually want "enter" to select the active person from the mention
                    list, rather than submit the comment.

                    Currently, the LexicalTypeaheadMenuPlugin uses COMMAND_PRIORITY_LOW to select the
                    active option from the menu of people.

                    And of course, we can't use COMMAND_PRIORITY_EDITOR, because then a newline character
                    will be added, rather than submit the comment.

                    So for now, we have to do a hack: check the DOM to see if the menu of people
                    is open. If it is, don't respond to the event. If it's not open, submit the comment.

                    There's already a merged PR that fixes this issue: https://github.com/facebook/lexical/pull/2885

                    That increases the priority of the command listeners in LexicalTypeaheadMenuPlugin
                    to use COMMAND_PRIORITY_NORMAL.

                    That change is just not currently included in a release. Once it is, and when we
                    upgrade lexical to use that release in our code, we can get rid of this DOM-checking hack.
                    */
                    const menuIsOpen =
                        document.querySelectorAll('[data-mentions-menu] > li').length > 0;
                    if (menuIsOpen) {
                        return false;
                    }
                    /* END HACK */

                    if (canPublish) {
                        handlePublishComment();
                    }
                    // handle the event, prevent default
                    // so that enter "submits" the comment
                    // and doesn't create a newline
                    e.preventDefault();
                    return true;
                },
                COMMAND_PRIORITY_LOW,
            ),
        );
    }, [editor, handlePublishComment, canPublish]);

    return (
        showing && (
            <div className={`${styles['add-comment__footer']} mt-l5-12`}>
                {/*
            This component is removed from the DOM when the editor is blurred.
            If the buttons are clicked, the editor will blur. In order to
            fire the button events before the blur (before the buttons are removed),
            we need to use onMouseDown rather than onClick
            */}
                <PrimaryButton
                    text="Comment"
                    onMouseDown={handlePublishComment}
                    disabled={!canPublish}
                    size={BUTTON_SIZES.SM}
                />
            </div>
        )
    );
});

export default CommentActionsPlugin;
